import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flipper } from 'react-flip-toolkit';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { IEditAutomationTemplateRequest, ILocationState, IModalContext } from '../../../models';
import {
	EditAutomationTemplateContext,
	EditAutomationTemplateItemIdPrefix,
	EditAutomationTemplateStepsContext,
	AutomationEditorStep,
	IEditAutomationTemplateContext,
	IEditAutomationTemplateStepsContext,
	isSwitchStep,
} from '../../../models/Automations';
import { useErrorMessages, useFab, useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import {
	useDuplicateAutomationTemplateMutation,
	useAutomationTemplateSetNameMutation,
	useDeleteAutomationTemplateMutation,
	useCommitAutomationDraftMutation,
	useDiscardAutomationDraftMutation,
} from '../../../queries/Automation/mutations/template';
import {
	AutomationDisableCancelModal,
	AutomationDisableCancelSelection,
} from '../../components/automation/AutomationDisableCancelModal';
import { AutomationTriggerCard } from '../../components/automation/AutomationTriggerCard';
import { EditAutomationTemplateItemBumpers } from '../../components/automation/EditAutomationTemplateItemBumpers';
import { EditAutomationTemplateSteps } from '../../components/automation/EditAutomationTemplateSteps';
import {
	ConfirmationDialog,
	DefaultDeleteConfirmationOptions,
	DefaultDiscardChangesConfirmationOptions,
	IConfirmationDialogOption,
} from '../../components/ConfirmationDialog';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { MultiContainerHeader } from '../../components/MultiContainerHeader';
import { NavigationBreadcrumbsBar } from '../../components/NavigationBreadcrumbsBar';
import { ScrollView } from '../../components/ScrollView';
import { AutomationTemplateStepIndicatorIcon } from '../../components/svgs/icons/AutomationTemplateStepIndicator';
import { DangerIcon } from '../../components/svgs/icons/DangerIcon';
import { LightningBoltIcon } from '../../components/svgs/icons/LightningBoltIcon';
import { PlusIcon } from '../../components/svgs/icons/PlusIcon';
import { TrashIcon } from '../../components/svgs/icons/TrashIcon';
import { WarningIcon } from '../../components/svgs/icons/WarningIcon';
import { TextInput } from '../../components/TextInput';
import { TextInputModal } from '../../components/TextInputModal';
import { baseStyleSheet } from '../../styles/styles';
import { INavigationItemProps } from '../MainContainer';
import { useAutomationEditorSteps, useSegmentActions, useStepActions } from './hooks';
import { styleSheet } from './styles';

export interface IEditAutomationTemplateHeaderCallbacks {
	onDisableButtonClicked(): void;
	showDeleteConfirmation(): void;
}

interface IProps extends INavigationItemProps, IModalContext {
	automationTemplate?: Api.AutomationTemplateViewModelType;
	className?: string;
	hideDisableOption?: boolean;
	onDraftUpdated?(): void;
	onRenderHeader?(callbacks: IEditAutomationTemplateHeaderCallbacks): React.ReactNode;
	readonly?: boolean;
	styles?: StyleDeclarationValue[];
	impersonationContext?: Api.IImpersonationContext;
	onAutomationDeleted: () => void;
	onAutomationPublished: () => void;
	onAutomationDisabled: () => void;
}

const PublishConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isCta: true,
		representedObject: true,
		title: 'Okay & Publish',
	},
];

export function EditAutomationTemplate(props: IProps) {
	const {
		automationTemplate: propsAutomationTemplate,
		className,
		styles,
		routeContainerClassName,
		hideDisableOption,
		onRenderHeader,
		readonly,
		impersonationContext: propsImpersonationContext,
		onDraftUpdated: propsOnDraftUpdated,
		onAutomationDeleted,
		onAutomationPublished,
		onAutomationDisabled,
	} = props;

	// Hooks
	const history = useHistory();
	const location = useLocation<ILocationState<Api.IAutomationTemplate, IEditAutomationTemplateRequest>>();
	const params = useParams<{ id: string }>();
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const fab = useFab();
	const { logEvent, logApiError } = useEventLogging('EditAutomationTemplate');

	// Derive templateId early to avoid duplicate API calls
	// Priority: props > location state > URL params
	const templateId = useMemo(() => {
		if (propsAutomationTemplate?.id) {
			return propsAutomationTemplate.id;
		}
		if (location?.state?.model?.automationTemplate?.id) {
			return location.state.model.automationTemplate.id;
		}
		return params.id;
	}, [propsAutomationTemplate?.id, location?.state?.model?.automationTemplate?.id, params.id]);

	// Derive impersonation context
	const impersonationContext = useMemo(() => {
		return propsAutomationTemplate?.impersonationContext || propsImpersonationContext;
	}, [propsAutomationTemplate?.impersonationContext, propsImpersonationContext]);

	// State
	const [automationTemplate, setAutomationTemplate] = useState<Api.AutomationTemplateViewModelType | undefined>(
		propsAutomationTemplate
	);
	const [loading, setLoading] = useState(true);
	const [name, setName] = useState<string | undefined>();
	const [lastSaveMoment, setLastSaveMoment] = useState<moment.Moment | undefined>();
	const [showDiscardChangesConfirmation, setShowDiscardChangesConfirmation] = useState(false);
	const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
	const [showDisableConfirmation, setShowDisableConfirmation] = useState(false);
	const [showDuplicationNameInputModal, setShowDuplicationNameInputModal] = useState(false);
	const [showPublishConfirmation, setShowPublishConfirmation] = useState(false);
	const [hasDraftVersion, setHasDraftVersion] = useState(!!propsAutomationTemplate?.hasDraftVersion);

	// Refs
	const stepsContainerRef = useRef<Flipper>(null);
	// Track template ID to allow reload when template changes
	const currentTemplateIdRef = useRef<string | undefined>();

	// Custom hooks for step management - use derived templateId to avoid duplicate fetches
	const editorSteps = useAutomationEditorSteps({
		templateId,
		impersonationContext,
	});

	const stepActions = useStepActions({
		automationTemplateId: automationTemplate?.id || '',
		steps: editorSteps.steps,
		automationTemplateTrigger:
			automationTemplate?.draftVersion?.trigger || automationTemplate?.publishedVersion?.trigger,
		impersonationContext,
		onStepsChanged: updatedSteps => {
			editorSteps.setStepsState(updatedSteps);
		},
		createEmptyStep: () => editorSteps.addStep(),
	});

	const segmentActions = useSegmentActions({
		automationTemplateId: automationTemplate?.id || '',
		trigger:
			automationTemplate?.draftVersion?.trigger ||
			automationTemplate?.publishedVersion?.trigger ||
			({} as Api.IAutomationTrigger),
		impersonationContext,
	});

	// Create stable callbacks for context - no refs needed
	const executeActionForStep = useCallback(
		async (step: AutomationEditorStep, action: string) => {
			switch (action) {
				case 'copy':
					await stepActions.copyStep(step);
					break;
				case 'delete':
					await stepActions.deleteStep(step);
					break;
				case 'move-up':
					stepActions.moveStepUp(step);
					break;
				case 'move-down':
					stepActions.moveStepDown(step);
					break;
				default:
					break;
			}
		},
		[stepActions]
	);

	const addSegment = useCallback(
		async (deleteFirstEmptyStep?: boolean) => {
			const currentSteps = editorSteps.steps;
			const firstStep = currentSteps?.[0];
			const existingSwitchStep = currentSteps?.find(s => isSwitchStep(s.automationStep))?.automationStep as
				| Api.ISwitchAutomationStep
				| undefined;

			if (deleteFirstEmptyStep && currentSteps?.length === 1 && !firstStep?.automationStep?.id) {
				await editorSteps.removeStep(firstStep);
			}

			await segmentActions.addSegment(existingSwitchStep);

			if (automationTemplate) {
				await editorSteps.loadSteps(automationTemplate, true);
			}
		},
		[segmentActions, editorSteps, automationTemplate]
	);

	// Mutations
	const setNameMutation = useAutomationTemplateSetNameMutation({
		onSuccess: () => {
			setLastSaveMoment(moment());
			setHasDraftVersion(true);
			propsOnDraftUpdated?.();
		},
		onError: error => {
			logApiError('AutomationTemplateSetName-Error', error);
			errorMessages.pushApiError(error);
			setLoading(false);
		},
	});

	const commitDraftMutation = useCommitAutomationDraftMutation({
		onSuccess: () => {
			toast.success('Automation template published.');
			onAutomationPublished();
		},
		onError: error => {
			logApiError('CommitDraft-Error', error);
			errorMessages.pushApiError(error);
			setLoading(false);
		},
	});

	const discardDraftMutation = useDiscardAutomationDraftMutation({
		onSuccess: () => {
			toast.success('Pending changes were discarded.');
			loadTemplate();
		},
		onError: error => {
			logApiError('Discard-Error', error);
			errorMessages.pushApiError(error);
			setLoading(false);
		},
	});

	const deleteTemplateMutation = useDeleteAutomationTemplateMutation({
		onSuccess: () => {
			toast.success('Automation template deleted.');
			onAutomationDeleted();
		},
		onError: error => {
			logApiError('Delete-Error', error);
			errorMessages.pushApiError(error);
			setLoading(false);
		},
	});

	const duplicateTemplateMutation = useDuplicateAutomationTemplateMutation({
		onSuccess: createdTemplate => {
			const newTemplate = new Api.AutomationTemplateViewModel(userSession, createdTemplate);
			const locationState: ILocationState<Api.IAutomationTemplate, IEditAutomationTemplateRequest> = {
				model: {
					automationTemplate: newTemplate,
				},
			};
			history?.replace(`/Automations/${newTemplate.id}`, locationState);
			loadTemplate(newTemplate);
		},
		onError: error => {
			logApiError('DuplicateAutomationTemplate-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	// Computed values
	const steps = editorSteps.steps;

	const canEdit = useMemo(() => {
		return !readonly && !!automationTemplate?.canEdit;
	}, [readonly, automationTemplate?.canEdit]);

	const isUpdatingStep = useMemo(() => {
		return (steps || []).filter(x => !!x.automationStep).some(x => x.automationStep.isBusy);
	}, [steps]);

	// Context values
	const editAutomationTemplateContext = useMemo<IEditAutomationTemplateContext>(
		() => ({
			canEdit,
			onDraftUpdated: propsOnDraftUpdated,
			onChildTemplatesLoaded: () => {
				// Check if any switch steps have child templates with draft versions
				const switchSteps = automationTemplate?.published?.steps.filter(
					(x: Api.IAutomationStep) => x._type === 'SwitchAutomationStep'
				) as Api.ISwitchAutomationStep[] | undefined;

				// For now, just check if we have any switch steps with automation template IDs
				// The actual template loading would happen elsewhere
				if (switchSteps && switchSteps.length > 0) {
					// This is a placeholder - the actual check would need to load the templates
					// and check their draft versions, but that's handled by the hooks
				}
			},
			parentAutomationTemplate: automationTemplate,
		}),
		[canEdit, propsOnDraftUpdated, automationTemplate]
	);

	// First memoize the data value with stable callbacks
	const editorStepsValue = useMemo(
		() => ({
			steps: editorSteps.steps,
			canReorderSteps: editorSteps.canReorderSteps,
			canReoderSteps: editorSteps.canReorderSteps,
			isLoaded: !editorSteps.isLoading,
			addStep: editorSteps.addStep,
			removeStep: editorSteps.removeStep,
			load: editorSteps.loadSteps,
			getIncompleteSteps: editorSteps.getIncompleteSteps,
			updateStep: editorSteps.updateStep,
			setShowRequiredFieldError: editorSteps.setShowRequiredFieldError,
			setStepsState: editorSteps.setStepsState,
			automationTemplate,
			impersonationContext,
			automationTemplateTrigger:
				automationTemplate?.draftVersion?.trigger || automationTemplate?.publishedVersion?.trigger,
			executeActionForStep,
			addSegment,
		}),
		[
			editorSteps.steps,
			editorSteps.canReorderSteps,
			editorSteps.isLoading,
			editorSteps.addStep,
			editorSteps.removeStep,
			editorSteps.loadSteps,
			editorSteps.getIncompleteSteps,
			editorSteps.updateStep,
			editorSteps.setShowRequiredFieldError,
			editorSteps.setStepsState,
			automationTemplate,
			impersonationContext,
			executeActionForStep,
			addSegment,
		]
	);

	// Then wrap in context structure
	const editAutomationTemplateStepsContext = useMemo<IEditAutomationTemplateStepsContext>(
		() => ({
			editorStepElementRefs: {},
			editorSteps: editorStepsValue as any,
		}),
		[editorStepsValue]
	);

	// Stable reference to loadSteps to avoid dependency cycles
	const loadStepsRef = useRef(editorSteps.loadSteps);
	loadStepsRef.current = editorSteps.loadSteps;

	// Load template from React Query data - no duplicate API calls
	const loadTemplateFromQueryData = useCallback(
		async (templateData: Api.IAutomationTemplate) => {
			// Create ViewModel wrapper from query data for backwards compatibility
			// The ViewModel is automatically considered "loaded" when it has draft/published data
			const template = new Api.AutomationTemplateViewModel(userSession, templateData);
			if (impersonationContext) {
				template.impersonate(impersonationContext);
			}

			logEvent('AutomationTemplateLoad', { id: template.id });

			logEvent('EditAutomationTemplateStepsLoad');
			// Use reloadTemplate=false since we already have fresh data from React Query
			await loadStepsRef.current(template, false);

			setAutomationTemplate(template);
			setName(template.name);
			setHasDraftVersion(template.hasDraftVersion);
			setLoading(false);
		},
		[userSession, impersonationContext, logEvent]
	);

	// Stable reference to templateData to avoid dependency cycles
	const templateDataRef = useRef(editorSteps.templateData);
	templateDataRef.current = editorSteps.templateData;

	// Legacy load template function - only used for reload/duplicate scenarios
	const loadTemplate = useCallback(
		async (templateToLoad?: Api.AutomationTemplateViewModelType) => {
			let template = templateToLoad;

			if (!template) {
				const locationState = location?.state;
				if (locationState?.model) {
					template = locationState.model.automationTemplate;
				}
			}

			if (!template) {
				const id = params.id;
				if (id) {
					template = new Api.AutomationTemplateViewModel(userSession, {
						id,
						templateType: Api.TemplateType.Automation,
					} as Api.IAutomationTemplate);
					if (propsImpersonationContext) {
						template.impersonate(propsImpersonationContext);
					}
				}
			}

			// If we have query data, use it instead of calling load()
			if (templateDataRef.current && template?.id === templateDataRef.current.id) {
				await loadTemplateFromQueryData(templateDataRef.current);
				return;
			}

			const promise = template?.load();
			if (promise) {
				logEvent('AutomationTemplateLoad', { id: template.id });

				try {
					await promise;
					logEvent('EditAutomationTemplateStepsLoad');
					await loadStepsRef.current(template, false);

					setAutomationTemplate(template);
					setName(template.name);
					setHasDraftVersion(template.hasDraftVersion);
					setLoading(false);
				} catch (error) {
					logApiError('AutomationTemplateLoad-Error', error as Api.IOperationResultNoValue);
					errorMessages.pushApiError(error as Api.IOperationResultNoValue);
					setLoading(false);
				}
			} else {
				setLoading(false);
				errorMessages.push({
					messages: ['Unable to load. An automation template id is required.'],
				});
			}
		},
		[
			location,
			params.id,
			userSession,
			propsImpersonationContext,
			logEvent,
			logApiError,
			errorMessages,
			loadTemplateFromQueryData,
		]
	);

	// Watch for query data and initialize template when available
	const hasInitializedRef = useRef(false);
	const loadTemplateFromQueryDataRef = useRef(loadTemplateFromQueryData);
	loadTemplateFromQueryDataRef.current = loadTemplateFromQueryData;
	// Track the last processed dataUpdatedAt to detect actual data changes
	const lastTemplateDataUpdatedAtRef = useRef<number | undefined>();

	useEffect(() => {
		// Load template when query data becomes available or template changes
		const templateData = editorSteps.templateData;
		const isLoading = editorSteps.isTemplateLoading;
		const dataUpdatedAt = editorSteps.templateDataUpdatedAt;

		const templateChanged = currentTemplateIdRef.current !== templateData?.id;
		const dataRefreshed = dataUpdatedAt && lastTemplateDataUpdatedAtRef.current !== dataUpdatedAt;

		if (templateData && !isLoading) {
			if (!hasInitializedRef.current || templateChanged || dataRefreshed) {
				hasInitializedRef.current = true;
				currentTemplateIdRef.current = templateData.id;
				lastTemplateDataUpdatedAtRef.current = dataUpdatedAt;
				loadTemplateFromQueryDataRef.current(templateData);
			}
		}
	}, [editorSteps.templateData, editorSteps.isTemplateLoading, editorSteps.templateDataUpdatedAt]);

	// Handle query errors
	useEffect(() => {
		if (editorSteps.isTemplateError && editorSteps.templateError) {
			logApiError('AutomationTemplateLoad-Error', editorSteps.templateError as Api.IOperationResultNoValue);
			errorMessages.pushApiError(editorSteps.templateError as Api.IOperationResultNoValue);
			setLoading(false);
		}
	}, [editorSteps.isTemplateError, editorSteps.templateError, logApiError, errorMessages]);

	// Handle case where no templateId is available
	useEffect(() => {
		if (!templateId && !propsAutomationTemplate) {
			setLoading(false);
			errorMessages.push({
				messages: ['Unable to load. An automation template id is required.'],
			});
		}
	}, [templateId, propsAutomationTemplate, errorMessages]);

	// Lifecycle: componentDidMount - only set up FAB, don't trigger load
	useEffect(() => {
		const disposer = fab.registerContext({
			appearance: {
				hidden: true,
			},
		});

		return () => {
			disposer();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Handlers
	const onAddStepClicked = useCallback(async () => {
		try {
			await editorSteps.addStep();
			propsOnDraftUpdated?.();
		} catch (error) {
			logApiError('AddStep-Error', error);
			errorMessages.pushApiError(error);
		}
	}, [editorSteps, propsOnDraftUpdated, logApiError, errorMessages]);

	const onAddSegmentClicked = useCallback(async () => {
		try {
			const firstStep = steps?.[0];
			const deleteFirstEmptyStep = steps?.length === 1 && !firstStep?.automationStep?.id;
			await addSegment(deleteFirstEmptyStep);
			propsOnDraftUpdated?.();
		} catch (error) {
			logApiError('AddSegment-Error', error);
			errorMessages.pushApiError(error);
		}
	}, [steps, addSegment, propsOnDraftUpdated, logApiError, errorMessages]);

	const onStepSaved = useCallback(() => {
		setLastSaveMoment(moment());
		setHasDraftVersion(true);
		propsOnDraftUpdated?.();
	}, [propsOnDraftUpdated]);

	const onNameInputChanged = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setName(e.target.value);
	}, []);

	const onNameInputBlur = useCallback(() => {
		if (!name || !automationTemplate?.id) {
			return;
		}
		logEvent('AutomationTemplateSetName', { id: automationTemplate.id });
		setNameMutation.mutate({
			id: automationTemplate.id,
			name,
			impersonationContext: automationTemplate.impersonationContext,
		});
	}, [name, automationTemplate, logEvent, setNameMutation]);

	const onPublishButtonClicked = useCallback(() => {
		setShowPublishConfirmation(true);
	}, []);

	const publish = useCallback(async () => {
		if (!automationTemplate?.id) {
			errorMessages.push({
				messages: ['Please save your automation before enabling it.'],
			});
			return;
		}

		try {
			// Look for errors and scroll to them
			const incompleteSteps = await editorSteps.getIncompleteSteps();
			const firstIncompleteStep = incompleteSteps[0];
			if (firstIncompleteStep) {
				toast.error('Please complete all required fields before publishing.');
				editorSteps.setShowRequiredFieldError?.(firstIncompleteStep.uuid, true);
				const elementId = `${EditAutomationTemplateItemIdPrefix}-${firstIncompleteStep.automationStep.id}`;
				document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth' });
				return;
			}

			setLoading(true);
			logEvent('CommitDraft', { id: automationTemplate.id });
			commitDraftMutation.mutate({
				id: automationTemplate.id,
				impersonationContext: automationTemplate.impersonationContext,
			});
		} catch (error) {
			logApiError('Publish-Error', error as Api.IOperationResultNoValue);
			errorMessages.push({
				messages: ['An error occurred while publishing the automation.'],
			});
		}
	}, [automationTemplate, editorSteps, logEvent, logApiError, commitDraftMutation, errorMessages]);

	const onPublishConfirmationDialogRequestClose = useCallback(
		(result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
			setShowPublishConfirmation(false);
			if (!!result?.representedObject && !cancel) {
				publish();
			}
		},
		[publish]
	);

	const showDiscardChanges = useCallback(() => {
		setShowDiscardChangesConfirmation(true);
	}, []);

	const onDiscardChangesConfirmationDialogRequestClose = useCallback(
		(result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
			setShowDiscardChangesConfirmation(false);
			if (!!result?.isDestructive && !cancel && automationTemplate?.id) {
				setLoading(true);
				logEvent('DiscardChanges', { id: automationTemplate.id });
				discardDraftMutation.mutate({
					id: automationTemplate.id,
					impersonationContext: automationTemplate.impersonationContext,
				});
			}
		},
		[automationTemplate, logEvent, discardDraftMutation]
	);

	const showDeleteConfirmationHandler = useCallback(() => {
		setShowDeleteConfirmation(true);
	}, []);

	const onDeleteConfirmationDialogRequestClose = useCallback(
		(result?: IConfirmationDialogOption<boolean>, cancel?: boolean) => {
			setShowDeleteConfirmation(false);
			if (!!result?.isDestructive && !cancel && automationTemplate?.id) {
				setLoading(true);
				logEvent('Delete', { id: automationTemplate.id });
				deleteTemplateMutation.mutate({
					id: automationTemplate.id,
					impersonationContext: automationTemplate.impersonationContext,
				});
			}
		},
		[automationTemplate, logEvent, deleteTemplateMutation]
	);

	const onDisableButtonClicked = useCallback(() => {
		if (automationTemplate?.stats?.inProgress >= 0) {
			setShowDisableConfirmation(true);
		}
	}, [automationTemplate]);

	const onDisableModalClosed = useCallback(
		(result: AutomationDisableCancelSelection) => {
			setShowDisableConfirmation(false);
			if (result !== AutomationDisableCancelSelection.DoNothing) {
				onAutomationDisabled();
			}
		},
		[onAutomationDisabled]
	);

	const onSaveAsNewClicked = useCallback(() => {
		setShowDuplicationNameInputModal(true);
	}, []);

	const onDuplicateAutomationNameInputModalRequestClose = useCallback(
		(result?: string, cancel?: boolean) => {
			setShowDuplicationNameInputModal(false);
			if (!cancel && !!result && automationTemplate?.id) {
				logEvent('DuplicateAutomation', {
					id: automationTemplate.id,
					nameLength: result.length,
				});
				duplicateTemplateMutation.mutate({
					id: automationTemplate.id,
					name: result,
					impersonationContext: automationTemplate.impersonationContext,
				});
			}
		},
		[automationTemplate, logEvent, duplicateTemplateMutation]
	);

	// Render helpers
	const renderTriggerIndicator = useCallback(() => {
		return (
			<div className={css(styleSheet.stepIndicator)}>
				<AutomationTemplateStepIndicatorIcon disableInnerCircle={true} className={css(styleSheet.triggerIndicator)}>
					<LightningBoltIcon className={css(styleSheet.triggerIndicatorIcon)} />
				</AutomationTemplateStepIndicatorIcon>
				<div className={css(styleSheet.triggerIndicatorHorizontalLine)} />
				<div className={css(styleSheet.triggerIndicatorVerticalLine)} />
			</div>
		);
	}, []);

	const renderStepsFooter = useCallback(() => {
		if (!canEdit) {
			return null;
		}

		const addStepButton = (
			<button className={css(baseStyleSheet.horizontalStack, styleSheet.addStepButton)} onClick={onAddStepClicked}>
				<PlusIcon />
				<span>Add a Step</span>
			</button>
		);

		const renderAddSegmentButton = (title?: string) => (
			<button className={css(baseStyleSheet.horizontalStack, styleSheet.addStepButton)} onClick={onAddSegmentClicked}>
				<PlusIcon />
				<span>{title || 'Add Segment'}</span>
			</button>
		);

		return (
			<div className={css(baseStyleSheet.horizontalStack, styleSheet.stepsFooter)}>
				{steps?.every(x => !!x.automationStep?._type && !isSwitchStep(x.automationStep)) || steps?.length === 0 ? (
					<>
						{addStepButton}
						{renderAddSegmentButton()}
					</>
				) : steps?.length === 1 && !steps[0]?.automationStep?.id ? (
					renderAddSegmentButton('Add Segment Instead')
				) : steps?.some(x => isSwitchStep(x.automationStep)) ? (
					renderAddSegmentButton()
				) : null}
			</div>
		);
	}, [canEdit, steps, onAddStepClicked, onAddSegmentClicked]);

	const renderAutosaveInfo = useCallback(() => {
		return (
			<div className={css(styleSheet.autosaveInfo)}>
				{!!lastSaveMoment && `Auto saved on ${lastSaveMoment.format('MM/DD/YYYY hh:mma')}`}
			</div>
		);
	}, [lastSaveMoment]);

	const renderPublishButton = useCallback(() => {
		const isDisabled =
			automationTemplate?.isBusy ||
			isUpdatingStep ||
			steps?.some(x => !x.automationStep?.id) ||
			steps?.length === 0 ||
			!hasDraftVersion ||
			!name;

		if (automationTemplate?.scope === Api.TemplateScope.Industry && !canEdit && !hasDraftVersion) {
			return (
				<>
					<button className={css(baseStyleSheet.ctaButton)} onClick={onSaveAsNewClicked}>
						<span>Save as New Template</span>
					</button>
					{!!showDuplicationNameInputModal && (
						<TextInputModal
							cta='Duplicate'
							modalProps={{
								isOpen: !!showDuplicationNameInputModal,
								onRequestClose: onDuplicateAutomationNameInputModalRequestClose,
							}}
							placeholderText='New Automation name'
							title={`Duplicate Automation: "${automationTemplate?.name}"`}
						/>
					)}
				</>
			);
		}

		if (canEdit) {
			return (
				<button
					className={css(automationTemplate?.hasPublishedVersion ? baseStyleSheet.ctaButton : styleSheet.enableButton)}
					disabled={isDisabled}
					onClick={!isDisabled ? onPublishButtonClicked : undefined}
				>
					<span>
						{automationTemplate?.hasPublishedVersion && automationTemplate?.status !== Api.TemplateStatus.Disabled
							? 'Publish Changes'
							: 'Enable Automation'}
					</span>
				</button>
			);
		}

		return null;
	}, [
		automationTemplate,
		isUpdatingStep,
		steps,
		hasDraftVersion,
		name,
		canEdit,
		showDuplicationNameInputModal,
		onSaveAsNewClicked,
		onDuplicateAutomationNameInputModalRequestClose,
		onPublishButtonClicked,
	]);

	const renderHeader = useCallback(() => {
		return (
			<div className={css(styleSheet.headerFooter)}>
				{renderAutosaveInfo()}
				<div className={css(baseStyleSheet.horizontalStack)}>
					{!loading &&
						(onRenderHeader ? (
							onRenderHeader({
								onDisableButtonClicked,
								showDeleteConfirmation: showDeleteConfirmationHandler,
							})
						) : (
							<>
								{hasDraftVersion && (
									<div className={css(styleSheet.unpublished)}>
										<DangerIcon className={css(styleSheet.dangerIcon)} />
										unpublished changes
									</div>
								)}
								{renderPublishButton()}
								{!!canEdit && automationTemplate?.hasPublishedVersion && hasDraftVersion && (
									<button
										className={css(baseStyleSheet.horizontalStack, baseStyleSheet.ctaButtonDestructiveSolid)}
										disabled={automationTemplate.isBusy || isUpdatingStep}
										onClick={showDiscardChanges}
									>
										<span>Discard Changes</span>
									</button>
								)}
								{!!canEdit && (
									<>
										{automationTemplate?.hasPublishedVersion &&
											automationTemplate?.status !== Api.TemplateStatus.Disabled &&
											!hideDisableOption && (
												<button
													className={css(baseStyleSheet.ctaButtonDestructive)}
													disabled={automationTemplate.isBusy || isUpdatingStep}
													onClick={onDisableButtonClicked}
												>
													<span>Disable Automation</span>
												</button>
											)}
										<button
											className={css(baseStyleSheet.horizontalStack, baseStyleSheet.ctaButtonDestructive)}
											disabled={automationTemplate.isBusy || isUpdatingStep}
											onClick={showDeleteConfirmationHandler}
										>
											<TrashIcon />
											<span>Delete</span>
										</button>
									</>
								)}
							</>
						))}
				</div>
			</div>
		);
	}, [
		renderAutosaveInfo,
		loading,
		onRenderHeader,
		onDisableButtonClicked,
		showDeleteConfirmationHandler,
		hasDraftVersion,
		renderPublishButton,
		automationTemplate,
		isUpdatingStep,
		showDiscardChanges,
		canEdit,
		hideDisableOption,
	]);

	const locationName = loading
		? 'Loading...'
		: `${automationTemplate?.publishedStepReferences?.length && automationTemplate.publishedStepReferences.length > 0 ? 'Edit' : 'Create New'} Automation`;

	return (
		<EditAutomationTemplateContext.Provider value={editAutomationTemplateContext}>
			<EditAutomationTemplateStepsContext.Provider value={editAutomationTemplateStepsContext}>
				<ScrollView
					className={`${css(styleSheet.scrollView, ...(styles || []))} edit-automation-template ${className || ''}`}
					contentClassName={routeContainerClassName || undefined}
					hasFixedHeader={true}
					header={renderHeader()}
				>
					<MultiContainerHeader
						appBarHeader={<NavigationBreadcrumbsBar currentLocationName={locationName} />}
						fullscreenHeader={locationName}
					/>
					<div className={css(styleSheet.body)}>
						<div className={css(styleSheet.bodyBackground)} />
						<div className={css(styleSheet.bodyContent)}>
							<article className={css(styleSheet.nameContainer)}>
								<h2 className={css(styleSheet.sectionTitle)}>
									Automation Name: &nbsp;
									<span className={css(baseStyleSheet.required)}>*</span>
								</h2>
								<TextInput
									className={css(styleSheet.nameInput)}
									disabled={loading || !canEdit}
									inputId='edit-automation-template-name-input'
									onBlur={onNameInputBlur}
									onChange={onNameInputChanged}
									type='text'
									value={name || ''}
								/>
								<div className={css(styleSheet.nameInputHint)}>
									Example: &quot;Leads Email Drip&quot;, or &quot;New Client To Do List&quot;
								</div>
							</article>
							<EditAutomationTemplateItemBumpers onRenderLeftBumperContent={renderTriggerIndicator}>
								<AutomationTriggerCard
									automationTemplate={automationTemplate as any}
									impersonationContext={impersonationContext}
									steps={steps}
								/>
							</EditAutomationTemplateItemBumpers>
							<EditAutomationTemplateSteps ref={stepsContainerRef} onStepSaved={onStepSaved}>
								{renderStepsFooter()}
							</EditAutomationTemplateSteps>
							{loading && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />}
						</div>
					</div>
					{!!canEdit && (
						<>
							{!!showDeleteConfirmation && (
								<ConfirmationDialog
									icon={<WarningIcon />}
									modalProps={{
										isOpen: !!showDeleteConfirmation,
										onRequestClose: onDeleteConfirmationDialogRequestClose,
									}}
									options={DefaultDeleteConfirmationOptions}
									title='Are you sure you want to delete this automation template?'
								/>
							)}
							{!!showPublishConfirmation && (
								<ConfirmationDialog
									icon={<WarningIcon />}
									modalProps={{
										isOpen: !!showPublishConfirmation,
										onRequestClose: onPublishConfirmationDialogRequestClose,
									}}
									options={PublishConfirmationOptions}
									title={
										<span>
											Changes will apply to future
											<br />
											automations in progress.
										</span>
									}
								>
									<div style={{ textAlign: 'center' }}>
										Contacts that have existing automation in progress
										<br />
										will finish out the previous version.
									</div>
								</ConfirmationDialog>
							)}
							{!!showDiscardChangesConfirmation && (
								<ConfirmationDialog
									icon={<WarningIcon />}
									modalProps={{
										isOpen: !!showDiscardChangesConfirmation,
										onRequestClose: onDiscardChangesConfirmationDialogRequestClose,
									}}
									options={DefaultDiscardChangesConfirmationOptions}
									title='Are you sure you want to discard changes?'
								/>
							)}
							{!!showDisableConfirmation && (
								<AutomationDisableCancelModal
									automationTemplate={automationTemplate as any}
									impersonationContext={impersonationContext}
									isOpen={showDisableConfirmation}
									onRequestClose={onDisableModalClosed}
								/>
							)}
						</>
					)}
				</ScrollView>
			</EditAutomationTemplateStepsContext.Provider>
		</EditAutomationTemplateContext.Provider>
	);
}
